import React, { useCallback, useEffect, useRef, useState } from 'react';
import TpDialog from '../../teacher_payments/shared/tp_dialog';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';
import { fetch_teacher_attrs } from '../../teacher_payments/api';
import { ist_day_label } from '../../teacher_payments/format';
import { ist_day } from '../../teacher_payments/shared/ist';
import {
  earnings_reason_error,
  last_change_text,
  single_payload
} from '../../teacher_payments/overview/earnings_logic';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import usePayTimeWarning from '../../../util_components/pay_time_warning/use_pay_time_warning';
import {
  DETAIL_EFFECT,
  with_pay_time_ack
} from '../../../util_components/pay_time_warning/pay_time_warning_rules';
import './payment_attributes.css';

// payment-attributes card; grant-only module, so a 403 on load hides the card entirely.

// A half-typed <input type="date"> reports '', so only an explicit "clear" may send no date.
export const live_date_to_send = ({ profile_live_at, clear }) => {
  if (clear) return { date: '' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(profile_live_at || '')) {
    return { error: 'Enter a complete date, or tick "Clear the live date" to remove it.' };
  }
  return { date: profile_live_at };
};

// "GST registered" as read: the rate the server reports (the teacher's own, else the rules'), never a guessed 5%.
export const gst_registered_text = (registered, rate_pct) => {
  if (!registered) return 'No';
  const rate = Number(rate_pct);
  return rate_pct != null && Number.isFinite(rate) ? `Yes (GST ${rate}%)` : 'Yes';
};

export const GST_LOCKED_HINT = "Set from the teacher's approved payment details.";

// A save refusal as {text, gst_locked}: GST_FROM_PAYMENT_DETAILS means the teacher has approved details,
// which this page cannot otherwise see, so the GST select is locked from then on.
export const attrs_save_error = (e, fallback = 'Failed to save payment attributes') => {
  const body = e?.response?.data || {};
  return {
    text: body.reason || body.message || fallback,
    gst_locked: body.error_code === 'GST_FROM_PAYMENT_DETAILS'
  };
};

// settings are absent on an older backend: then they are neither shown nor sent.
export const LIVE_DATE_ONLY_VIEW = 'LIVE_DATE_ONLY';

export const payroll_settings_known = (attrs) =>
  attrs?.on_payroll !== undefined && attrs?.no_free_pass !== undefined;

// "Free Pass" as read: the server's reason sentence when it does not apply (payroll, No Free Pass, staff email).
export const free_pass_applies_text = (data) =>
  data?.free_pass_exclusion ? data.free_pass_exclusion_text || 'Does not apply' : 'Applies';

export const PAYROLL_HINT =
  'No Earnings or payment pages in the teacher app (also when signed in as the teacher), never paid through this system, no Free Pass.';
export const NO_FREE_PASS_HINT = 'No Free Pass Days earned, used or paid out.';

const PaymentAttributes = ({ teacher_uuid, on_saved }) => {
  const [data, setData] = useState(null); // { attrs, can_edit }
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [liveForm, setLiveForm] = useState(null); // { profile_live_at, clear, reason } while the live-date editor is open
  const [earningsForm, setEarningsForm] = useState(null); // { visible, reason } while the Earnings confirm is open
  const [gstLocked, setGstLocked] = useState(false);
  const livePw = usePayTimeWarning(); // A-52: the live-date warning step, in place of the form
  // Only the latest load may land: a slow answer for the previous teacher must not overwrite this one.
  const seq = useRef(0);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setError('');
    fetch_teacher_attrs(teacher_uuid)
      .then((res) => {
        if (mine !== seq.current) return;
        // malformed envelope must surface the error state, not hide the card forever
        if (!res?.data?.data) throw new Error('unexpected response shape');
        setData(res.data.data);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        if (e?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        console.error('payment_attributes load failed', e);
        setError('Failed to load payment attributes');
      });
  }, [teacher_uuid]);

  useEffect(() => {
    // reset before loading so a teacher switch never shows the previous teacher's attrs
    setData(null);
    setError('');
    setForbidden(false);
    setGstLocked(false);
    if (teacher_uuid) load();
  }, [teacher_uuid, load]);

  if (!teacher_uuid || forbidden) return null;
  if (error) {
    return (
      <div className="tp-card tp-pa">
        <div className="tp-pa-body tp-pa-state">
          <span className="tp-err">{error}</span>
          <button type="button" className="tp-btn-out" onClick={load}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="tp-card tp-pa">
        <div className="tp-pa-body tp-pa-state">
          <span className="tp-sub">Loading payment attributes…</span>
        </div>
      </div>
    );
  }

  const { can_edit, extra_participant_category: extraParticipantCategory } = data;
  // The onboarding role is sent the live date only (no attrs), so the card shows just that row.
  const liveOnly = data.view === LIVE_DATE_ONLY_VIEW;
  const attrs = data.attrs || {};
  const canEditLive = data.can_edit_live_at ?? can_edit;
  // Server flag when known (absent on an older backend = editable); the post-409 lock still covers a stale load.
  const gstSelectLocked = gstLocked || data.gst_from_payment_details === true;
  const payrollKnown = payroll_settings_known(attrs);

  const openEdit = () => {
    setForm({
      is_gst_registered: attrs.is_gst_registered,
      free_pass_balance: String(attrs.free_pass_balance),
      on_payroll: Number(attrs.on_payroll) || 0,
      no_free_pass: Number(attrs.no_free_pass) || 0,
      reason: ''
    });
    setModalError('');
    setEditing(true);
  };

  const closeModal = () => {
    if (!saving) setEditing(false);
  };

  // client-side diff — mirrors the backend's no-op semantics so we can skip empty saves
  const formChanged = () => {
    if (Number(form.is_gst_registered) !== attrs.is_gst_registered) return true;
    if (form.free_pass_balance !== '' && Number(form.free_pass_balance) !== attrs.free_pass_balance)
      return true;
    if (payrollKnown && Number(form.on_payroll) !== Number(attrs.on_payroll)) return true;
    if (payrollKnown && Number(form.no_free_pass) !== Number(attrs.no_free_pass)) return true;
    return false;
  };

  const save = () => {
    setModalError('');
    if (!formChanged()) {
      setEditing(false);
      return;
    }
    if (form.free_pass_balance !== '') {
      const n = Number(form.free_pass_balance);
      if (!Number.isInteger(n) || n < 0 || n > 12) {
        setModalError('Free Pass balance must be a whole number between 0 and 12.');
        return;
      }
    }
    if (!form.reason.trim()) {
      setModalError('A reason is required — it goes to the payment audit trail.');
      return;
    }
    setSaving(true);
    // The backend refuses addl_participant_teacher_type / training_batch_date (the category comes
    // from Profile Live At, edited separately) — never sent.
    const payload = {
      teacher_uuid,
      is_gst_registered: Number(form.is_gst_registered),
      reason: form.reason.trim()
    };
    // blank balance = leave unchanged (never coerce '' to 0 — that would silently zero it)
    if (form.free_pass_balance !== '') {
      payload.free_pass_balance = Number(form.free_pass_balance);
    }
    if (payrollKnown) {
      payload.on_payroll = Number(form.on_payroll);
      payload.no_free_pass = Number(form.no_free_pass);
    }
    const url = get_api_url('/v2/cms_user/teacher_payments/teacher_attrs', false, true);
    post_api(url, payload, true)
      .then(() => {
        setEditing(false);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        const refusal = attrs_save_error(e);
        if (refusal.gst_locked) {
          setGstLocked(true);
          // Back to the saved value, so a Free Pass change can still be saved on its own.
          setForm((f) => ({ ...f, is_gst_registered: attrs.is_gst_registered }));
        }
        setModalError(refusal.text);
      })
      .finally(() => setSaving(false));
  };

  const openLiveEdit = () => {
    setLiveForm({
      profile_live_at: extraParticipantCategory?.profile_live_at || '',
      clear: false,
      reason: ''
    });
    setModalError('');
  };

  const closeLiveEdit = () => {
    if (saving) return;
    livePw.clear();
    setLiveForm(null);
  };

  // ack: the pay-time acknowledgement (A-52); null on the first send
  const saveLiveDate = (ack = null) => {
    setModalError('');
    const { date, error: dateError } = live_date_to_send(liveForm);
    if (dateError) {
      setModalError(dateError);
      return;
    }
    if ((extraParticipantCategory?.profile_live_at || '') === date) {
      setLiveForm(null);
      return;
    }
    if (!liveForm.reason.trim()) {
      setModalError('A reason is required — it goes to the payment audit trail.');
      return;
    }
    setSaving(true);
    const url = get_api_url(
      '/v2/cms_user/teacher_payments/teacher_attrs/profile_live_at',
      false,
      true
    );
    // '' clears the date (the server then uses the profile's created date; neither date = Old)
    const body = with_pay_time_ack(
      { teacher_uuid, profile_live_at: date, reason: liveForm.reason.trim() },
      ack
    );
    post_api(url, body, true)
      .then(() => {
        livePw.clear();
        setLiveForm(null);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        if (livePw.catch_warning(e)) return;
        livePw.clear();
        setModalError(e?.response?.data?.reason || 'Failed to save the profile live date');
      })
      .finally(() => setSaving(false));
  };

  // the teacher app's Earnings page, on or off per teacher (audited; no payment change).
  const openEarnings = () => {
    setEarningsForm({ visible: Number(attrs.earnings_visible) !== 1, reason: '' });
    setModalError('');
  };

  const closeEarnings = () => {
    if (!saving) setEarningsForm(null);
  };

  const saveEarnings = () => {
    setModalError('');
    const reasonError = earnings_reason_error(earningsForm.reason);
    if (reasonError) {
      setModalError(reasonError);
      return;
    }
    setSaving(true);
    const url = get_api_url('/v2/cms_user/teacher_payments/teacher_attrs', false, true);
    post_api(url, single_payload(teacher_uuid, earningsForm.visible, earningsForm.reason), true)
      .then(() => {
        setEarningsForm(null);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        setModalError(e?.response?.data?.reason || 'Failed to change Earnings visibility');
      })
      .finally(() => setSaving(false));
  };

  const earningsKnown = attrs.earnings_visible !== undefined; // absent on an older backend
  const earningsOn = Number(attrs.earnings_visible) === 1;
  const staffAccount = data.earnings_staff_account === true;
  const earningsVerb = earningsForm && earningsForm.visible ? 'Show Earnings' : 'Hide Earnings';

  const categoryWord = extraParticipantCategory?.category === 'NEW' ? 'New teacher' : 'Old teacher';
  // with no live date the created date decides; the server's label names that date.
  const categoryText =
    extraParticipantCategory?.basis === 'PROFILE_CREATED' && extraParticipantCategory?.label
      ? extraParticipantCategory.label
      : categoryWord;

  // A label/value field of the card: an optional small action beside the value, a muted hint under it.
  const field = ({ label, value, testid, action, hint, wide }) => (
    <div className={`tp-pa-field${wide ? ' tp-pa-wide' : ''}`}>
      <dt>{label}</dt>
      <dd>
        <span className="tp-pa-value" data-testid={testid}>
          {value}
        </span>
        {action || null}
      </dd>
      {hint ? <dd className="tp-pa-hint">{hint}</dd> : null}
    </div>
  );
  const reason_field = (value, on_change, placeholder) => (
    <div className="tp-field">
      <label htmlFor="tp-pa-reason">
        Reason <span className="tp-pa-req">*</span> (audit trail)
      </label>
      <textarea
        id="tp-pa-reason"
        className="tp-textarea"
        value={value}
        onChange={on_change}
        placeholder={placeholder}
      />
    </div>
  );
  const modal_error = modalError ? (
    <div className="tp-err" role="alert">
      {modalError}
    </div>
  ) : null;
  const footer = (on_cancel, on_save, label) => (
    <div className="tp-modal-ft">
      <button type="button" className="tp-btn-ghost" onClick={on_cancel} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="tp-btn" onClick={on_save} disabled={saving}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );

  return (
    <div className="tp-card tp-pa">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          {liveOnly ? 'Profile live date' : 'Payment attributes'}
        </span>
        {can_edit && !liveOnly ? (
          <span className="tp-card-hd-acts">
            <button type="button" className="tp-btn-out" onClick={openEdit}>
              Edit
            </button>
          </span>
        ) : null}
      </div>
      <dl className="tp-pa-body tp-pa-grid">
        {liveOnly
          ? null
          : field({
              label: 'GST registered',
              value: gst_registered_text(
                attrs.is_gst_registered,
                data.gst_rate_effective_pct ?? attrs.gst_rate_pct
              )
            })}
        {liveOnly ? null : field({ label: 'Free Pass balance', value: attrs.free_pass_balance })}
        {!liveOnly && payrollKnown
          ? field({
              label: 'Yoga teacher on payroll',
              value: Number(attrs.on_payroll) === 1 ? 'Yes' : 'No',
              testid: 'pa-on-payroll'
            })
          : null}
        {!liveOnly && payrollKnown
          ? field({
              label: 'Free Pass',
              value: free_pass_applies_text(data),
              testid: 'pa-free-pass-applies'
            })
          : null}
        {earningsKnown && !liveOnly
          ? field({
              label: 'Teacher can see Earnings',
              value: earningsOn ? 'Yes' : 'No',
              action:
                can_edit && !(staffAccount && !earningsOn) ? (
                  <button type="button" className="tp-btn-out tp-pa-act" onClick={openEarnings}>
                    {earningsOn ? 'Hide Earnings' : 'Show Earnings'}
                  </button>
                ) : null,
              hint:
                staffAccount && !earningsOn
                  ? 'Earnings is not available for staff accounts.'
                  : last_change_text(data.earnings_visible_last_change)
            })
          : null}
        {/* the category is read-only, derived from Profile Live At; absent on an older backend (compat). */}
        {extraParticipantCategory
          ? field({
              label: 'Profile live date (IST)',
              value: ist_day_label(extraParticipantCategory.profile_live_at) || 'Not set',
              action: canEditLive ? (
                <button type="button" className="tp-btn-out tp-pa-act" onClick={openLiveEdit}>
                  Edit live date
                </button>
              ) : null
            })
          : null}
        {extraParticipantCategory
          ? field({
              label: 'Teacher category',
              value: categoryText,
              wide: true,
              hint: 'Live on or before 8 Jun 2026: Old. From 9 Jun 2026: New. No live date: the profile created date decides; neither: Old. Decides extra-participant pay.'
            })
          : null}
      </dl>

      <TpDialog
        show={!!earningsForm}
        title={
          earningsForm && earningsForm.visible
            ? 'Show Earnings to this teacher?'
            : 'Hide Earnings from this teacher?'
        }
        size="sm"
        busy={saving}
        on_close={closeEarnings}
      >
        {earningsForm ? (
          <div className="tp-modal tp-pa-modal">
            <p className="tp-pa-lead">
              {earningsForm.visible
                ? 'The teacher will see the Earnings page in the teacher app.'
                : 'The Earnings page will be hidden in the teacher app.'}{' '}
              No payment changes.
            </p>
            {reason_field(
              earningsForm.reason,
              (e) => setEarningsForm({ ...earningsForm, reason: e.target.value }),
              'e.g. Earnings pilot, batch 1'
            )}
            {modal_error}
            {footer(closeEarnings, saveEarnings, earningsVerb)}
          </div>
        ) : null}
      </TpDialog>

      <TpDialog
        show={!!liveForm}
        title="Edit profile live date"
        size="sm"
        busy={saving}
        on_close={closeLiveEdit}
      >
        {liveForm && livePw.warning ? (
          <div className="tp-modal tp-pa-modal">
            <PayTimeWarningStep
              warning={livePw.warning}
              detail={DETAIL_EFFECT}
              busy={saving}
              changeLabel="Change date"
              actions="tp"
              onAcknowledge={(ack) => saveLiveDate(ack)}
              onChange={livePw.clear}
            />
            {modal_error}
          </div>
        ) : null}
        {liveForm && !livePw.warning ? (
          <div className="tp-modal tp-pa-modal">
            <div className="tp-field">
              <label htmlFor="tp-pa-live">Profile live date (India date)</label>
              <input
                id="tp-pa-live"
                className="tp-input"
                type="date"
                max={ist_day()}
                value={liveForm.profile_live_at}
                disabled={liveForm.clear}
                onChange={(e) => setLiveForm({ ...liveForm, profile_live_at: e.target.value })}
              />
            </div>
            {extraParticipantCategory?.profile_live_at ? (
              <label className="tp-pa-check">
                <input
                  type="checkbox"
                  checked={liveForm.clear}
                  onChange={(e) => setLiveForm({ ...liveForm, clear: e.target.checked })}
                />
                Clear the live date (the profile created date then decides)
              </label>
            ) : null}
            <p className="tp-pa-lead">Open months are recalculated.</p>
            {reason_field(
              liveForm.reason,
              (e) => setLiveForm({ ...liveForm, reason: e.target.value }),
              'e.g. Ops sheet: profile went live 9 Jun 2026'
            )}
            {modal_error}
            {footer(closeLiveEdit, () => saveLiveDate(), 'Save date')}
          </div>
        ) : null}
      </TpDialog>

      <TpDialog
        show={editing && !!form}
        title="Edit payment attributes"
        size="md"
        busy={saving}
        on_close={closeModal}
      >
        {form ? (
          <div className="tp-modal tp-pa-modal">
            <div className="tp-pa-form-grid">
              <div className="tp-field">
                <label htmlFor="tp-pa-gst">GST registered</label>
                <select
                  id="tp-pa-gst"
                  className="tp-select"
                  value={form.is_gst_registered}
                  onChange={(e) => setForm({ ...form, is_gst_registered: e.target.value })}
                  disabled={gstSelectLocked}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
                {gstSelectLocked ? <span className="tp-hint">{GST_LOCKED_HINT}</span> : null}
              </div>
              <div className="tp-field">
                <label htmlFor="tp-pa-fp">Free Pass balance (0–12)</label>
                <input
                  id="tp-pa-fp"
                  className="tp-input"
                  type="number"
                  min={0}
                  max={12}
                  step={1}
                  value={form.free_pass_balance}
                  onChange={(e) => setForm({ ...form, free_pass_balance: e.target.value })}
                />
              </div>
              {payrollKnown ? (
                <div className="tp-field">
                  <label htmlFor="tp-pa-payroll">Yoga teacher on payroll</label>
                  <select
                    id="tp-pa-payroll"
                    className="tp-select"
                    aria-label="Yoga teacher on payroll"
                    value={form.on_payroll}
                    onChange={(e) => setForm({ ...form, on_payroll: e.target.value })}
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                  <span className="tp-hint">{PAYROLL_HINT}</span>
                </div>
              ) : null}
              {payrollKnown ? (
                <div className="tp-field">
                  <label htmlFor="tp-pa-nofp">No Free Pass</label>
                  <select
                    id="tp-pa-nofp"
                    className="tp-select"
                    aria-label="No Free Pass"
                    value={form.no_free_pass}
                    onChange={(e) => setForm({ ...form, no_free_pass: e.target.value })}
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                  <span className="tp-hint">{NO_FREE_PASS_HINT}</span>
                </div>
              ) : null}
            </div>
            {payrollKnown ? (
              <p className="tp-pa-lead">
                Payroll and No Free Pass changes recalculate open months; closed months never
                change.
              </p>
            ) : null}
            {reason_field(
              form.reason,
              (e) => setForm({ ...form, reason: e.target.value }),
              'e.g. GST certificate received / seeded from Ops sheet'
            )}
            {modal_error}
            {footer(closeModal, save, 'Save changes')}
          </div>
        ) : null}
      </TpDialog>
    </div>
  );
};

export default PaymentAttributes;
